// Media tab — drag-drop upload + the creator's asset library. Uploads
// route by size to the public repo; click an item to drop its markdown
// into the active draft. Images preview inline; everything else shows a
// kind chip.

import { useRef, useState } from "react";
import { useMediaStore } from "../../store/mediaStore";
import { useComposeStore } from "../../store/composeStore";
import { mediaMarkdown, type MediaRef } from "../../lib/github-media";

function fmtSize(n: number): string {
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(0)} KB`;
  return `${(n / 1024 / 1024).toFixed(1)} MB`;
}

function previewUrl(item: MediaRef, siteUrl: string | null): string {
  // Inline assets are repo-relative; to preview before the site is
  // deployed we point at the public repo's raw content is unreliable, so
  // for inline images we just show the kind chip. Release assets have an
  // absolute URL and preview directly.
  return item.storage === "release" ? item.url : (siteUrl ? `${siteUrl}/${item.url}` : "");
}

export function MediaPane() {
  const items = useMediaStore((s) => s.items);
  const uploading = useMediaStore((s) => s.uploading);
  const progress = useMediaStore((s) => s.progress);
  const error = useMediaStore((s) => s.error);
  const upload = useMediaStore((s) => s.upload);
  const remove = useMediaStore((s) => s.remove);
  const insertIntoBody = useComposeStore((s) => s.insertIntoBody);

  const [drag, setDrag] = useState(false);
  const fileRef = useRef<HTMLInputElement>(null);

  return (
    <div className="md-pane">
      <div
        className={"md-drop" + (drag ? " md-drop-on" : "")}
        onDragOver={(e) => { e.preventDefault(); setDrag(true); }}
        onDragLeave={() => setDrag(false)}
        onDrop={(e) => { e.preventDefault(); setDrag(false); if (e.dataTransfer.files.length) void upload(e.dataTransfer.files); }}
        onClick={() => fileRef.current?.click()}
      >
        {uploading ? (progress ?? "uploading…") : "Drop files here, or click to choose"}
        <div className="md-drop-sub">images, video, audio · small files commit, large go to Releases</div>
        <input ref={fileRef} type="file" multiple hidden onChange={(e) => { if (e.target.files?.length) void upload(e.target.files); e.target.value = ""; }} />
      </div>

      {error && <div className="md-error">{error}</div>}

      <div className="md-list">
        {items.length === 0 && !uploading && <div className="md-empty">No media yet.</div>}
        {items.map((m) => {
          const url = previewUrl(m, null);
          return (
            <div key={m.id} className="md-item">
              <div className="md-thumb">
                {m.kind === "image" && m.storage === "release"
                  ? <img src={url} alt={m.filename} />
                  : <span className="md-kind">{m.kind}</span>}
              </div>
              <div className="md-meta">
                <div className="md-name" title={m.filename}>{m.filename}</div>
                <div className="md-sub">{fmtSize(m.size)} · {m.storage}</div>
              </div>
              <div className="md-actions">
                <button className="md-insert" title="insert into draft" onClick={() => insertIntoBody(mediaMarkdown(m))}>insert</button>
                <button className="md-del" title="remove from library" onClick={() => void remove(m.id)}>×</button>
              </div>
            </div>
          );
        })}
      </div>

      <style>{`
        .md-pane { display: flex; flex-direction: column; height: 100%; min-height: 0; }
        .md-drop {
          flex: none; margin: var(--space-3); padding: var(--space-4);
          border: 1.5px dashed var(--line-strong); border-radius: var(--radius-1);
          text-align: center; font-size: 13px; color: var(--ink-soft); cursor: pointer;
        }
        .md-drop:hover, .md-drop-on { border-color: var(--teal); color: var(--teal); background: var(--teal-dim); }
        .md-drop-sub { font-size: 11px; color: var(--ink-faint); margin-top: 4px; }
        .md-error { margin: 0 var(--space-3) var(--space-2); padding: 8px 10px; background: var(--rust-bg); border: 1px solid rgba(180,80,42,0.3); border-radius: var(--radius-1); color: var(--rust); font-size: 12px; user-select: text; }
        .md-list { flex: 1; overflow: auto; padding: 0 var(--space-2) var(--space-2); display: flex; flex-direction: column; gap: 2px; }
        .md-empty { color: var(--ink-faint); font-size: 12px; padding: var(--space-3); }
        .md-item { display: flex; align-items: center; gap: var(--space-2); padding: var(--space-2); border-radius: var(--radius-1); }
        .md-item:hover { background: var(--paper-sunk); }
        .md-thumb { flex: none; width: 40px; height: 40px; border-radius: 6px; overflow: hidden; background: var(--paper-sunk); display: flex; align-items: center; justify-content: center; }
        .md-thumb img { width: 100%; height: 100%; object-fit: cover; }
        .md-kind { font-family: var(--font-mono); font-size: 9px; text-transform: uppercase; color: var(--ink-faint); }
        .md-meta { flex: 1; min-width: 0; }
        .md-name { font-size: 12px; color: var(--ink); overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
        .md-sub { font-family: var(--font-mono); font-size: 10px; color: var(--ink-faint); }
        .md-actions { flex: none; display: flex; align-items: center; gap: 4px; }
        .md-insert { font-family: var(--font-mono); font-size: 10px; color: var(--teal); background: var(--teal-dim); border: none; border-radius: 6px; padding: 4px 8px; cursor: pointer; }
        .md-del { width: 20px; height: 20px; border: none; background: transparent; color: var(--ink-faint); font-size: 16px; line-height: 1; border-radius: 5px; cursor: pointer; }
        .md-del:hover { background: var(--rust-bg); color: var(--rust); }
      `}</style>
    </div>
  );
}
